import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { isNodeEvnProduction } from '@prophecy/utils/env';
import { isNil, omit, pick } from 'lodash-es';
import React, { useMemo } from 'react';
import { LoadingDIcon, XCircleIcon } from '../Icons';
import { renderWithTooltip } from '../Tooltip';
import { Text } from '../Typography/Text';
import { UnderlineHint } from '../Typography/UnderlineHint';
import { renderRequiredLabel } from './Label';
import { StyledInputContainer, StyledIconWrapper, StyledLabel, InputWrap, StyledHelpText, StyledInputRoot, StyledAddOnWrapper, StyledLabelWithHint } from './styled';
import { tokens } from './tokens';
import { InputVariants, Placement, InputSize } from './types';
const SHELL_PROP_KEYS = [
    'variant',
    'inputSize',
    'required',
    'label',
    'hint',
    'hintWithUnderline',
    'tooltip',
    'helpText',
    'style',
    'className',
    'disabled',
    'noBorder',
    'noBoxShadow',
    'loading',
    'icon',
    'iconPlacement',
    'addOn',
    'addOnPlacement',
    'addOnSeparator',
    'title',
    'placeholder'
];
/**
 * Picks shell-relevant visual/layout props from a broader props object.
 * Use with spread: `<InputShell {...getShellProps(props)} ...>`
 */ export function getShellProps(props) {
    return pick(props, SHELL_PROP_KEYS);
}
/**
 * Strips shell-only props from a rest object so they don't leak to the inner `<input>`.
 * Use after destructuring input-specific props: `const restProps = stripShellProps(rawRest)`
 */ export function stripShellProps(props) {
    return omit(props, SHELL_PROP_KEYS);
}
export function InputShell({ variant = InputVariants.primary, inputSize = InputSize.m, type = 'text', required = false, label, hint, hintWithUnderline, tooltip, helpText, style, className, disabled, noBorder, noBoxShadow, loading, icon, iconPlacement = Placement.right, addOn, addOnPlacement = Placement.left, addOnSeparator = true, title, placeholder, showClear, onClear, onClick, inputId, helpTextId, maxLength, valueLength, value, children }) {
    const showIcon = icon && !(showClear && iconPlacement === Placement.right) && !(loading && iconPlacement === Placement.left);
    if (!isNodeEvnProduction()) {
        if (inputSize !== InputSize.l && title) {
            throw new Error('title prop is only available for large size input');
        }
    }
    let inputUI = /*#__PURE__*/ _jsxs(InputWrap, {
        align: "start",
        alignY: "start",
        direction: "vertical",
        addOnPlacement: addOn ? addOnPlacement : undefined,
        iconPlacement: icon ? iconPlacement : undefined,
        "data-placeholder": placeholder,
        "data-value": value,
        children: [
            title && /*#__PURE__*/ _jsx(Text, {
                level: "xxs",
                color: tokens.Input.Title.color,
                children: title
            }),
            children
        ]
    });
    if (!isNil(tooltip)) {
        inputUI = /*#__PURE__*/ _jsx(_Fragment, {
            children: renderWithTooltip(inputUI, tooltip)
        });
    }
    if (!label && hint) {
        inputUI = /*#__PURE__*/ _jsx(StyledLabelWithHint, {
            hint: hint,
            children: inputUI
        });
    }
    const labelUIWrapper = useMemo(()=>{
        const labelUI = label ? /*#__PURE__*/ _jsx(StyledLabel, {
            htmlFor: inputId,
            children: label
        }) : null;
        if (label && hintWithUnderline) {
            return /*#__PURE__*/ _jsx(UnderlineHint, {
                hint: hintWithUnderline,
                required: required,
                children: labelUI
            });
        }
        if (label && hint) {
            return /*#__PURE__*/ _jsx(StyledLabelWithHint, {
                level: "xs",
                hint: hint,
                children: labelUI
            });
        }
        return renderRequiredLabel({
            label: labelUI,
            required
        });
    }, [
        hint,
        hintWithUnderline,
        inputId,
        label,
        required
    ]);
    return /*#__PURE__*/ _jsxs(StyledInputRoot, {
        variant: variant,
        size: inputSize,
        direction: "vertical",
        gap: tokens.Root.gap,
        style: style,
        className: className,
        onClick: onClick,
        children: [
            labelUIWrapper,
            /*#__PURE__*/ _jsxs(StyledInputContainer, {
                type: type,
                variant: variant,
                inputSize: inputSize,
                disabled: disabled,
                noBorder: noBorder,
                noBoxShadow: noBoxShadow,
                children: [
                    inputUI,
                    showClear && /*#__PURE__*/ _jsx(StyledIconWrapper, {
                        iconPlacement: Placement.right,
                        addOnPlacement: addOn ? addOnPlacement : undefined,
                        children: /*#__PURE__*/ _jsx(XCircleIcon, {
                            type: "solid",
                            onClick: onClear
                        })
                    }),
                    loading && /*#__PURE__*/ _jsx(StyledIconWrapper, {
                        iconPlacement: Placement.left,
                        addOnPlacement: addOn ? addOnPlacement : undefined,
                        children: /*#__PURE__*/ _jsx(LoadingDIcon, {
                            type: "extended",
                            spin: true
                        })
                    }),
                    showIcon && /*#__PURE__*/ _jsx(StyledIconWrapper, {
                        iconPlacement: iconPlacement,
                        addOnPlacement: addOn ? addOnPlacement : undefined,
                        children: icon
                    }),
                    addOn && /*#__PURE__*/ _jsx(StyledAddOnWrapper, {
                        size: inputSize,
                        $variant: variant,
                        iconPlacement: icon ? iconPlacement : undefined,
                        addOnPlacement: addOnPlacement,
                        addOnSeparator: !!addOnSeparator,
                        children: addOn
                    })
                ]
            }),
            helpText && /*#__PURE__*/ _jsxs(StyledHelpText, {
                id: helpTextId,
                variant: variant,
                direction: "horizontal",
                align: "space-between",
                role: variant === InputVariants.error ? 'alert' : undefined,
                children: [
                    helpText,
                    type !== 'number' && maxLength && maxLength > 0 ? /*#__PURE__*/ _jsx("span", {
                        children: (valueLength ?? 0) + '/' + maxLength
                    }) : null
                ]
            })
        ]
    });
}
